// ─── User & Auth ──────────────────────────────────────────────────
export interface UserProfile {
  uid: string;
  email: string;
  displayName: string;
  photoURL?: string;
  createdAt: Date;
  subscriptionStatus: 'trial' | 'active' | 'cancelled' | 'expired';
  subscriptionTier: 'free' | 'pro' | 'coach';
  trialEndsAt?: Date;
  stripeCustomerId?: string;
  isAdmin?: boolean;
}

// ─── Shots & Sessions ─────────────────────────────────────────────
export type ShotType =
  | 'pot'
  | 'miss'
  | 'safety'
  | 'foul'
  | 'longPot'       // long pot that went in
  | 'longMiss'      // long pot that missed
  | 'restPot'       // rest shot that went in
  | 'restMiss'      // rest shot that missed
  | 'snookered';    // received a snooker — outcome tracked via escapeOutcome

export type BallColor = 'red' | 'yellow' | 'green' | 'brown' | 'blue' | 'pink' | 'black';
export type SafetyOutcome = 'good' | 'bad';
export type EscapeOutcome = 'escaped' | 'fouled';

export const BALL_POINTS: Record<BallColor, number> = {
  red: 1, yellow: 2, green: 3, brown: 4, blue: 5, pink: 6, black: 7,
};

export const BALL_HEX: Record<BallColor, string> = {
  red: '#c0392b', yellow: '#f1c40f', green: '#27ae60',
  brown: '#8B4513', blue: '#3498db', pink: '#e07b7b', black: '#1a1a2e',
};

export interface Shot {
  id: string;
  type: ShotType;
  ball?: BallColor;
  points?: number;
  timestamp: number;
  frameNumber: number;
  breakNumber: number;
  breakRunningTotal: number;
  safetyOutcome?: SafetyOutcome;   // only set when type === 'safety'
  escapeOutcome?: EscapeOutcome;   // only set when type === 'snookered'
}

export interface BreakRecord {
  breakNumber: number;
  frameNumber: number;
  score: number;
  ballSequence: BallColor[];
  endedBy: 'miss' | 'safety' | 'foul' | 'end_of_frame' | 'end_of_session';
}

export interface Frame {
  frameNumber: number;
  breaks: BreakRecord[];
  highBreak: number;
  totalPots: number;
  totalMisses: number;
  totalSafeties: number;
  totalFouls: number;
}

export interface Session {
  id: string;
  uid: string;
  startedAt: Date;
  endedAt?: Date;
  type: 'match' | 'practice';
  shots: Shot[];
  frames: Frame[];
  stats?: SessionStats;
}

// ─── Stats ────────────────────────────────────────────────────────
export interface SessionStats {
  sessionId: string;
  uid: string;
  date: Date;
  sessionType: 'match' | 'practice';

  // Potting
  totalPots: number;
  totalMisses: number;
  potRate: number;

  // Breaks
  totalBreaks: number;
  highBreak: number;
  avgBreak: number;
  breaksOver20: number;
  breaksOver50: number;
  breaksOver100: number;
  pointsPerVisit: number;

  // Safety
  totalSafeties: number;
  goodSafeties: number;
  safetySuccessRate: number;
  safetyRate: number;

  // Fouls
  totalFouls: number;

  // Snooker escapes
  snookeredCount: number;
  snookerEscapes: number;
  snookerEscapeRate: number;

  // Long potting
  totalLongAttempts: number;
  totalLongPots: number;
  longPotRate: number;

  // Rest shots
  totalRestAttempts: number;
  totalRestPots: number;
  restSuccessRate: number;

  // Session metadata
  totalFrames: number;
  totalShots: number;
  sessionDurationMinutes: number;
}

export interface AggregatedStats {
  uid: string;
  updatedAt: Date;
  sessionCount: number;
  avgPotRate30d: number;
  avgBreak30d: number;
  avgSafetyRate30d: number;
  highBreakAllTime: number;
  currentTier: TierName;
  tierUpdatedAt: Date;
}

// ─── Tiers ────────────────────────────────────────────────────────
export type TierName = 'Beginner' | 'Amateur' | 'Advanced Amateur' | 'Semi-Pro' | 'Pro Candidate' | 'Professional';

export interface Tier {
  name: TierName;
  slug: string;
  color: string;
  minAvgBreak: number;
  minPotRate: number;
  minSafetyRate: number;
  description: string;
  proEquivalent: string;
}

// ─── Drills ───────────────────────────────────────────────────────
export type DrillType =
  | 'lineup' | 'long_pot' | 'colors_clearance'
  | 'baulk_colors' | 'safety_exchange' | 'break_building';

export interface DrillDefinition {
  id: DrillType;
  name: string;
  description: string;
  ballsInvolved: BallColor[];
  targetStat: string;
  icon: string;
}

export interface DrillAttempt {
  id: string;
  uid: string;
  drillId: DrillType;
  date: Date;
  successRate: number;
  potsMade: number;
  potsAttempted: number;
  durationSeconds: number;
  notes?: string;
}

// ─── Admin ────────────────────────────────────────────────────────
export interface AdminStats {
  totalUsers: number;
  activeSubscriptions: number;
  trialUsers: number;
  mrr: number;
  sessionsLast7d: number;
  newUsersLast7d: number;
}